// src/components/projects/ProjectCard.styles.ts
//
// One card, used for every project on the page. The `featured` project is the
// same component with `$featured` set — there is no second card type and no
// per-project branch anywhere in the markup.
//
// The visual vocabulary is lifted from SelectedWorkPreview.styles.ts (the Home
// page's own "Selected work" section) so the two read as one system: the same
// meta row, hairline technology tags, and the same hairline-border hover. The
// two emphasis levels map onto the two Home already uses — `radius.lg` on
// `cardBg` for the wide slot, `radius.md` on the page background for the rest.
import styled from 'styled-components';

import { minDevice } from '../../styles/breakpoints';

const monoStack = "ui-monospace, SFMono-Regular, 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace";

/**
 * Card surface. Both variants share one set of properties so the page cannot
 * drift between them; only the surface tokens and the hover distance differ.
 *
 * A plain styled.article, not a motion component: the reveal belongs to the
 * list item in ProjectGrid, so the card stays pure presentation.
 *
 * The hover is transform-only, which cannot reflow the page, and it is dropped
 * entirely under prefers-reduced-motion.
 */
export const CardRoot = styled.article<{ $featured: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.9rem;
  padding: 1.75rem;
  background: ${({ theme, $featured }) => ($featured ? theme.cardBg : theme.background)};
  border: ${({ theme, $featured }) => ($featured ? theme.cardBorder : `1px solid ${theme.borderColor}`)};
  border-radius: ${({ theme, $featured }) => ($featured ? theme.borderRadius.lg : theme.borderRadius.md)};
  transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1), border-color 0.35s ease;

  &:hover {
    transform: translateY(${({ $featured }) => ($featured ? '-4px' : '-3px')});
    border-color: ${({ theme }) => theme.accent};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: border-color 0.2s ease;

    &:hover {
      transform: none;
    }
  }
`;

/** Client, separator, category — the same meta row Home uses above a title. */
export const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
`;

/** Client name reads as the primary half of the meta row. */
export const ClientName = styled.span`
  font-family: ${monoStack};
  font-size: 0.68rem;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.headingColor};
`;

export const MetaDivider = styled.span`
  color: ${({ theme }) => theme.borderColor};
`;

export const CategoryText = styled.span`
  font-family: ${monoStack};
  font-size: 0.68rem;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.accent};
`;

/** Marks the emphasised project. Decorative: the wider slot already says which
 *  one it is, and the title below carries the meaning. */
export const FeaturedBadge = styled.span`
  margin-left: auto;
  padding: 0.2rem 0.5rem;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-family: ${monoStack};
  font-size: 0.62rem;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.accent};
  white-space: nowrap;
`;

/**
 * Card title. Sits directly under the page's h1, so it is an h2 — the cards
 * are the top-level content of this page, not a sub-section of a section
 * heading the way Home's preview cards are.
 */
export const CardTitle = styled.h2<{ $featured: boolean }>`
  margin: 0;
  font-size: ${({ $featured }) => ($featured ? '1.4rem' : '1.15rem')};
  line-height: 1.3;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.headingColor};
  /* Long platform names must wrap rather than widen the grid track. */
  overflow-wrap: break-word;

  @media ${minDevice.tablet} {
    font-size: ${({ $featured }) => ($featured ? '1.7rem' : '1.25rem')};
  }
`;

/** The summary stays the main read; the contribution below it is secondary. */
export const CardDescription = styled.p`
  margin: 0;
  max-width: 62ch;
  font-size: 0.95rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.text};
`;

/**
 * Contribution, set off by a left rule. This is Home's ContextBlock treatment,
 * reused rather than reinvented: it reads as supporting detail without needing
 * a second heading above it.
 */
export const Contribution = styled.p`
  margin: 0;
  max-width: 68ch;
  padding-left: 0.9rem;
  border-left: 2px solid ${({ theme }) => theme.borderColor};
  font-size: 0.9rem;
  line-height: 1.65;
  color: ${({ theme }) => theme.colors.secondary};
`;

/* Hairline tags rather than filled chips, so the technology list reads as
   metadata. Matches TechList / TechTag on the Home page. */

export const TechList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  /* Pushed to the bottom of the flex column so cards of differing text length
     still line their tag rows up across the grid row. */
  margin-top: auto;
  padding-top: 0.35rem;
`;

export const TechTag = styled.li`
  padding: 0.2rem 0.5rem;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-family: ${monoStack};
  font-size: 0.7rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.secondary};
  /* "Clean Architecture" and "Repository Pattern" are the longest tags; a long
     word must break inside the tag rather than overflow the card. */
  overflow-wrap: break-word;
`;
